import { useCallback, useSyncExternalStore } from "react"

/**
 * Persisted layout for the driver-chat widget panel: one column of one or two
 * windows, top to bottom. A window that loses its last tab disappears, so the
 * one under it moves up. Same single-tab `useSyncExternalStore` pattern as
 * `useDesktopMapHidden`; cross-tab `storage` sync is out of scope.
 */
export type BuiltinWidgetTabId =
  | "chat"
  | "automations"
  | "map"
  | "workTime"
  | "temperature"

/** Prefix for a dynamic "task details" tab, opened by a task widget's ↗
 * icon — not offered in the "+" add-widget menu, not persisted across a
 * reload (see `openTaskDetails`). */
const TASK_DETAILS_PREFIX = "taskDetails:"

type TaskDetailsTabId = `${typeof TASK_DETAILS_PREFIX}${string}`

export type WidgetTabId = BuiltinWidgetTabId | TaskDetailsTabId

export const isTaskDetailsId = (id: WidgetTabId): id is TaskDetailsTabId =>
  id.startsWith(TASK_DETAILS_PREFIX)

const DEFAULT_WIDGET_TABS: BuiltinWidgetTabId[] = [
  "chat",
  "automations",
  "map",
  "workTime",
]

export const ALL_WIDGET_TABS: BuiltinWidgetTabId[] = [
  ...DEFAULT_WIDGET_TABS,
  "temperature",
]

/** Translation keys; the tab strip calls `t()` on them at render. */
export const WIDGET_TAB_LABEL_KEYS = {
  chat: "chat.widgetTabs.labels.chat",
  automations: "chat.widgetTabs.labels.automations",
  map: "chat.widgetTabs.labels.map",
  workTime: "chat.widgetTabs.labels.workTime",
  temperature: "chat.widgetTabs.labels.temperature",
} as const satisfies Record<BuiltinWidgetTabId, string>

/** One stage of a task, for the `TaskDetailsTool` pane. */
export type TaskTimelineStep = {
  label: string
  status: "done" | "current" | "upcoming"
  /** A time / place / delta — include the day with any time that isn't
   * obviously today: "wt. 05:20", "ETA śr. 15:40", "brama 4", "+28 km". */
  detail?: string
  /** Marks an off-plan step (a detour) amber instead of green. */
  tone?: "warn"
}

/** A document attached to the order, listed on the task-details pane. */
export type TaskDocument = {
  fileName: string
  kind?: string
}

/** For a finished task — the same at-a-glance figures the Task Summary
 * widget shows, so the tool reads the same way. */
export type TaskDetailSummary = {
  pickup: { countryCode: string; postalCode: string; city?: string }
  delivery: { countryCode: string; postalCode: string; city?: string }
  durationMinutes: number
  distanceKm: number
  tollsEur: number
  durationDeltaMinutes?: number
  distanceDeltaKm?: number
  tollsDeltaEur?: number
  note?: string
  noteTone?: "positive" | "warning" | "neutral"
}

/** The full detail behind one task widget's ↗ icon: its stage timeline,
 * documents, and — once finished — the same figures as the Task Summary
 * card. */
export type TaskDetail = {
  taskStatus: "ongoing" | "done"
  routeLabel?: string
  dateLabel?: string
  /** Schedule health: minutes late (+) or early (−); 0 / undefined = on time. */
  scheduleDeltaMinutes?: number
  documents?: TaskDocument[]
  steps: TaskTimelineStep[]
  summary?: TaskDetailSummary
}

type TaskDetailsEntry = { label: string; detail: TaskDetail }

/** In-memory only — a task widget's ↗ opens a snapshot of that moment, not a
 * standing resource; a reload starts the panel over with the persisted
 * built-in tabs. Keyed by the same dynamic id used in `panes`/`active`, so
 * `sanitize` can tell a live tab from a stale one restored from storage. */
const taskDetailsRegistry = new Map<WidgetTabId, TaskDetailsEntry>()

/** The room whose task-details tabs are open — see `showRoom`. */
let taskDetailsRoom: string | null = null

export const getTaskDetailsEntry = (
  id: WidgetTabId,
): TaskDetailsEntry | undefined => taskDetailsRegistry.get(id)

/** One window of the panel: its tabs and the one it shows. */
export type WidgetWindow = { tabs: WidgetTabId[]; active: WidgetTabId }

/** Top window, bottom window. The widgets are laid out for the panel's full
 * width, so there is no second column. */
export type WindowIndex = 0 | 1

type WidgetTabsState = {
  /** Top to bottom; never empty, and no window without a tab. */
  windows: [WidgetWindow] | [WidgetWindow, WidgetWindow]
  /** Panel closed entirely (conversation takes the full width). */
  hidden: boolean
  knownTabs: BuiltinWidgetTabId[]
}

/** What a mutator hands to `write`: any windows, empty or with a stale
 * `active`, which `sanitize` then repairs. */
type Draft = Omit<WidgetTabsState, "windows"> & { windows: WidgetWindow[] }

/**
 * The panel a dispatcher gets before they rearrange it: the map on top, the
 * chat with the driver under it. Every built-in widget listed here is open;
 * one left out stays in the "+" menu until someone adds it.
 */
const DEFAULT_WIDGET_LAYOUT: Pick<WidgetTabsState, "windows"> & {
  /** Bump with every change here or to the saved shape: a saved layout
   * outranks the default, and only a new storage key brings the new default
   * to people who have one. */
  version: number
} = {
  version: 4,
  windows: [
    { tabs: ["map"], active: "map" },
    { tabs: ["chat", "automations", "workTime"], active: "chat" },
  ],
}

export const WIDGET_LAYOUT_STORAGE_KEY = `driverChatWidgetPanes.v${DEFAULT_WIDGET_LAYOUT.version}`

// Layouts saved under an older default are never read again.
try {
  localStorage.removeItem("driverChatWidgetPanes")
  for (let v = 2; v < DEFAULT_WIDGET_LAYOUT.version; v++)
    localStorage.removeItem(`driverChatWidgetPanes.v${v}`)
} catch {
  // storage unavailable — nothing to clean up
}

const DEFAULT_STATE: WidgetTabsState = {
  windows: DEFAULT_WIDGET_LAYOUT.windows,
  hidden: false,
  knownTabs: DEFAULT_WIDGET_TABS,
}

const subscribers = new Set<() => void>()

let cache: WidgetTabsState = DEFAULT_STATE
let cacheRaw: string | null = null

/** Canonical order, de-duplicated: built-ins first (their fixed order), then
 * any task-details tabs in the order they were given. */
const orderIds = (ids: WidgetTabId[]): WidgetTabId[] => {
  const builtins = ALL_WIDGET_TABS.filter((t) => ids.includes(t))
  const dynamic = new Set(ids.filter(isTaskDetailsId))
  return [...builtins, ...dynamic]
}

/**
 * The one place that repairs the panel: canonical order, no tab in two
 * windows, no empty window, and an active tab that its window actually
 * holds. Every write goes through here, so the mutators only have to say
 * which tabs moved where and which one takes focus.
 */
const sanitize = (value: unknown): WidgetTabsState => {
  if (!value || typeof value !== "object") return DEFAULT_STATE
  const raw = value as Record<string, unknown>
  // A task-details tab only survives sanitize while its payload is still in
  // the in-memory registry — a fresh page load starts with an empty registry,
  // so a stale reference from a previous session drops out on its own.
  const valid = (id: unknown): id is WidgetTabId =>
    typeof id === "string" &&
    (ALL_WIDGET_TABS.includes(id as BuiltinWidgetTabId) ||
      (isTaskDetailsId(id as WidgetTabId) &&
        taskDetailsRegistry.has(id as WidgetTabId)))

  const knownTabs = Array.isArray(raw.knownTabs)
    ? raw.knownTabs
    : DEFAULT_WIDGET_TABS
  const unseenTabs = DEFAULT_WIDGET_TABS.filter((id) => !knownTabs.includes(id))

  // A tab lives in one window only — the first window to claim it wins.
  const claimed = new Set<WidgetTabId>()
  const rawWindows = Array.isArray(raw.windows) ? raw.windows : []
  const windows: WidgetWindow[] = []
  rawWindows.forEach((rawWindow, i) => {
    const { tabs, active } = (rawWindow ?? {}) as Record<string, unknown>
    const saved: unknown[] = Array.isArray(tabs) ? tabs : []
    const stored = i === 0 ? [...saved, ...unseenTabs] : saved
    const ids = orderIds(stored.filter(valid).filter((id) => !claimed.has(id)))
    ids.forEach((id) => claimed.add(id))
    if (ids.length > 0)
      windows.push({
        tabs: ids,
        active: valid(active) && ids.includes(active) ? active : ids[0],
      })
  })

  const [top, bottom] = windows
  if (!top) return DEFAULT_STATE
  return {
    windows: bottom ? [top, bottom] : [top],
    hidden: Boolean(raw.hidden),
    knownTabs: DEFAULT_WIDGET_TABS,
  }
}

const read = (): WidgetTabsState => {
  try {
    const stored = localStorage.getItem(WIDGET_LAYOUT_STORAGE_KEY)
    if (stored !== cacheRaw) {
      cacheRaw = stored
      cache = stored ? sanitize(JSON.parse(stored)) : DEFAULT_STATE
    }
  } catch {
    cache = DEFAULT_STATE
  }
  return cache
}

const subscribe = (listener: () => void): (() => void) => {
  subscribers.add(listener)
  return () => {
    subscribers.delete(listener)
  }
}

const write = (next: Draft): void => {
  const sanitized = sanitize(next)
  try {
    cacheRaw = JSON.stringify(sanitized)
    localStorage.setItem(WIDGET_LAYOUT_STORAGE_KEY, cacheRaw)
  } catch {
    // storage unavailable — keep the in-memory copy so the UI still reacts
  }
  cache = sanitized
  subscribers.forEach((listener) => listener())
}

const allTabs = (s: WidgetTabsState): WidgetTabId[] =>
  s.windows.flatMap((w) => w.tabs)

const windowOf = (s: WidgetTabsState, id: WidgetTabId): number | null => {
  const i = s.windows.findIndex((w) => w.tabs.includes(id))
  return i === -1 ? null : i
}

/** New tabs open outside the map's window so they never cover it. Where the
 * map lives is the dispatcher's call — `DEFAULT_WIDGET_LAYOUT` only sets the
 * start. */
const MAP_TAB: BuiltinWidgetTabId = "map"

/** Where a newly opened tab goes: the first window without the map, so
 * opening something never covers it — the top window otherwise. */
const landingWindow = (c: WidgetTabsState): number => {
  const i = c.windows.findIndex((w) => !w.tabs.includes(MAP_TAB))
  return i === -1 ? 0 : i
}

/** `id` in window `to` and shown there, and in no other window. */
const putTab = (c: WidgetTabsState, to: number, id: WidgetTabId): Draft => ({
  ...c,
  windows: c.windows.map((w, i) => {
    const tabs = w.tabs.filter((t) => t !== id)
    return i === to
      ? { tabs: orderIds([...tabs, id]), active: id }
      : { ...w, tabs }
  }),
})

const focusTab = (c: WidgetTabsState, id: WidgetTabId): Draft => ({
  ...putTab(c, windowOf(c, id) ?? landingWindow(c), id),
  hidden: false,
})

/** Take `id` out of a single window into a new one above or below it. When
 * `id` was the window's only tab, the emptied window drops out in
 * `sanitize` and nothing changes. */
const splitOff = (
  c: WidgetTabsState,
  side: "above" | "below",
  id: WidgetTabId,
): Draft => {
  const [only] = c.windows
  const rest = { ...only, tabs: only.tabs.filter((t) => t !== id) }
  const alone = { tabs: [id], active: id }
  return {
    ...c,
    hidden: false,
    windows: side === "above" ? [alone, rest] : [rest, alone],
  }
}

export const useWidgetTabs = () => {
  const state = useSyncExternalStore(subscribe, read, read)

  const setActiveTab = useCallback((index: WindowIndex, id: WidgetTabId) => {
    const c = read()
    if (!c.windows[index]?.tabs.includes(id)) return
    write(putTab(c, index, id))
  }, [])

  const addTab = useCallback((id: WidgetTabId) => {
    write(focusTab(read(), id))
  }, [])

  /**
   * Open a closed widget in a specific window — the per-window "+" menu, which
   * only offers widgets that aren't open anywhere (one of each; moving an
   * open one is a tab drag).
   */
  const addTabToWindow = useCallback((index: WindowIndex, id: WidgetTabId) => {
    const c = read()
    if (windowOf(c, id) !== null || !c.windows[index]) return
    write(putTab(c, index, id))
  }, [])

  const removeTab = useCallback((id: WidgetTabId) => {
    const c = read()
    if (windowOf(c, id) === null) return
    if (allTabs(c).length <= 1) return // never close the last widget
    // A window left empty drops out, and one that lost its shown tab falls
    // back to its first one — both in `sanitize`.
    write({
      ...c,
      windows: c.windows.map((w) => ({
        ...w,
        tabs: w.tabs.filter((t) => t !== id),
      })),
    })
    if (isTaskDetailsId(id)) taskDetailsRegistry.delete(id)
  }, [])

  /**
   * The panel now shows `roomJid`. A task widget's ↗ opens a snapshot of the
   * room it was in (see `openTaskDetails`), so switching to another driver's
   * chat — even while the panel was hidden — drops every open task-details
   * tab (D2x). Showing the same room again, e.g. after hide/show, keeps them.
   * `sanitize` repairs the window an active tab was pulled out from.
   */
  const showRoom = useCallback((roomJid: string) => {
    if (roomJid === taskDetailsRoom) return
    taskDetailsRoom = roomJid
    const c = read()
    const ids = allTabs(c).filter(isTaskDetailsId)
    if (ids.length === 0) return
    write({
      ...c,
      windows: c.windows.map((w) => ({
        ...w,
        tabs: w.tabs.filter((id) => !isTaskDetailsId(id)),
      })),
    })
    ids.forEach((id) => taskDetailsRegistry.delete(id))
  }, [])

  /**
   * Open (or focus) a task widget's ↗ target — the task-details tool for one
   * order, as another tab in whichever window already holds it, or where
   * `addTab` would put it otherwise (so it never forces a split). Reveals the
   * panel if it was hidden, same as any other "open a widget" action.
   */
  const openTaskDetails = useCallback(
    (taskId: string, label: string, detail: TaskDetail) => {
      const id: WidgetTabId = `${TASK_DETAILS_PREFIX}${taskId}`
      taskDetailsRegistry.set(id, { label, detail })
      write(focusTab(read(), id))
    },
    [],
  )

  const openTabOnTop = useCallback((id: WidgetTabId) => {
    const c = read()
    write(c.windows.length > 1 ? focusTab(c, id) : splitOff(c, "above", id))
  }, [])

  /** Hide the whole panel (its single-window × / the driver-bar control). */
  const hidePanel = useCallback(() => {
    write({ ...read(), hidden: true })
  }, [])

  /**
   * The driver-bar control is a plain show/hide switch. Hiding keeps the
   * windows as they were, so the panel comes back exactly as it was left.
   */
  const togglePanel = useCallback(() => {
    const c = read()
    write({ ...c, hidden: !c.hidden })
  }, [])

  /**
   * Drag `id` onto the other window. Moving a window's last tab out closes
   * that window, which folds the two into one.
   */
  const moveTab = useCallback((index: WindowIndex, id: WidgetTabId) => {
    const c = read()
    const owner = windowOf(c, id)
    if (owner === null || owner === index || !c.windows[index]) return
    write(putTab(c, index, id))
  }, [])

  /** Drag `id` onto the top or bottom half of a single window: it opens in a
   * new window above or below the rest. */
  const splitWindow = useCallback(
    (side: "above" | "below", id: WidgetTabId) => {
      const c = read()
      if (c.windows.length > 1 || windowOf(c, id) === null) return
      write(splitOff(c, side, id))
    },
    [],
  )

  /**
   * Close a whole window (its × button). The bottom window's tabs fold back
   * into the top one; closing the top window closes its tabs and moves the
   * bottom one up, keeping the tab it had open.
   */
  const closeWindow = useCallback((index: WindowIndex) => {
    const c = read()
    const [top, bottom] = c.windows
    if (!bottom) return // the only window open
    if (index === 0) return write({ ...c, windows: [bottom] })
    write({
      ...c,
      windows: [
        { tabs: orderIds([...top.tabs, ...bottom.tabs]), active: top.active },
      ],
    })
  }, [])

  const reset = useCallback(() => write(DEFAULT_STATE), [])

  return {
    /** Every window's tabs, union — for the "add widget" checklist. */
    openTabs: orderIds(allTabs(state)),
    windows: state.windows,
    hidden: state.hidden,
    setActiveTab,
    closeWindow,
    hidePanel,
    addTab,
    addTabToWindow,
    removeTab,
    togglePanel,
    moveTab,
    splitWindow,
    openTaskDetails,
    showRoom,
    openTabOnTop,
    reset,
  }
}
